import React, { SyntheticEvent, useCallback, useState } from 'react'
import { Button, Form, Modal } from 'react-bootstrap'
import QuillEditor, { QuillToolbar } from '~/common/quill/QuillEditor'
import '@css/pages/community/CommunityEventEdit.scss'
import ReactDatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import TimeSelector from '~/common/TimeSelector'
import { AddEventDocument, EditEventDocument, Html, Maybe } from '~/api/generated/graphql'
import FormError from '~/common/FormError'
import { useWindowSize } from '~/common/hooks/useWindowSize'
import { asDate, asEvent, getStartOfCurrentDay, padDigit } from '~/utils'
import { elementClicked } from '~/common/EventLogger'
import TimeAgo from '~/common/TimeAgo'
import { Link } from 'react-router-dom'
import { EventRepostLink } from '~/common/RepostOverlay'
import { useMutation } from '@apollo/client'
import { useAuth } from '~/auth/Auth'

type EventUpdateProps = {
  eventStart: string
  eventEnd: string
  title: string
  location: string
  description?: string | null
  hidden: boolean
  date: string
  notify: boolean
  draft: boolean
  googleCalendar?: string | null
  isLivestream?: boolean
  qAndAEnabled?: boolean
  webinarId?: string | null
}

type CommunityEventEditProps = {
  communityId: string
  event?: Maybe<
    Partial<{
      eventStart?: string
      eventEnd?: string
      lastNotifiedTime?: Date
      title?: string
      draft?: boolean
      location?: Maybe<string>
      googleCalendar?: Maybe<string>
      isLivestream?: boolean
      livestreamWebinarId?: Maybe<string>
      qAndAEnabled?: boolean
      description?: Maybe<Html>
      eventId?: string
      reposts?: Maybe<Maybe<{ hidden?: boolean; eventId?: string }>[]>
    }>
  >
  onClose: () => void
  setToast?: (toast: string) => void
}

const CommunityEventEdit = ({ communityId, event, onClose, setToast }: CommunityEventEditProps) => {
  const { actingSysAdmin, authUserId } = useAuth()
  const [addEvent] = useMutation(AddEventDocument)
  const [editEvent] = useMutation(EditEventDocument)
  const [livestreamSelection, setLivestreamSelection] = useState<'direct' | 'webinar' | 'none'>(
    event?.isLivestream
      ? event.livestreamWebinarId && event.livestreamWebinarId != '1'
        ? 'webinar'
        : 'direct'
      : 'none'
  )
  const { isCondensedPortrait } = useWindowSize()

  const roundTime = (date: Date) => {
    const ms = 1000 * 60 * 15 // convert 15 m to ms
    return new Date(Math.ceil(date.getTime() / ms) * ms)
  }

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const [isDraft] = useState(event?.draft ?? false)
  const [startDate, setStartDate] = useState<Date>(asDate(event?.eventStart) ?? new Date())
  const [startTime, setStartTime] = useState<Date>(asDate(event?.eventStart) ?? roundTime(new Date()))
  const [endDate, setEndDate] = useState<Date>(asDate(event?.eventEnd) ?? new Date(Date.now() + 1000 * 60 * 60))
  const [endTime, setEndTime] = useState<Date>(
    asDate(event?.eventEnd) ?? roundTime(new Date(Date.now() + 1000 * 60 * 60))
  )
  const [title, setTitle] = useState<string>(event?.title ?? '')
  const [webinarId, setWebinarId] = useState<string>(
    event?.livestreamWebinarId && event?.livestreamWebinarId != '1' ? event?.livestreamWebinarId : ''
  )
  const [location, setLocation] = useState(event?.location)
  const [googleCalendar, setGoogleCalendar] = useState(event?.googleCalendar)
  const [googleCalendarError, setGoogleCalendarError] = useState<string | null>(null)
  const [description, setDescription] = useState(event?.description)
  const [titleError, setTitleError] = useState<boolean>(false)
  const [isLivestream, setIsLivestream] = useState(event?.isLivestream ?? false)
  const [qAndAEnabled, setQAndAEnabled] = useState(event?.qAndAEnabled ?? false)
  const [dateRangeError, setDateRangeError] = useState<boolean>(false)
  const [pastDateError, setPastDateError] = useState<boolean>(false)
  const [showNotifyDialog, setShowNotifyDialog] = useState(false)
  const [buttonsDisabled, setButtonsDisabled] = useState<boolean>(false)
  const reposts = event?.reposts?.filter(e => !e?.hidden).map(e => asEvent(e))
  const hasReposts = Boolean(reposts && reposts.length > 0)
  const handleDirectLivestream = useCallback(() => {
    setLivestreamSelection('direct')
    setIsLivestream(true)
    setWebinarId('')
  }, [setIsLivestream, setWebinarId, setLivestreamSelection])
  const handleWebinarLivestream = useCallback(() => {
    setLivestreamSelection('webinar')
    setIsLivestream(true)
    setWebinarId('')
  }, [setIsLivestream, setWebinarId, setLivestreamSelection])
  const handleNoLivestream = useCallback(() => {
    setLivestreamSelection('none')
    setIsLivestream(false)
    setWebinarId('')
  }, [setIsLivestream, setWebinarId, setLivestreamSelection])
  const formatDate = (date: Date, time: Date): string => {
    const combinedDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      time.getHours(),
      time.getMinutes()
    )
    return `${combinedDate.getUTCFullYear()}-${padDigit(combinedDate.getUTCMonth() + 1)}-${padDigit(
      combinedDate.getUTCDate()
    )} ${padDigit(combinedDate.getUTCHours())}:${padDigit(combinedDate.getUTCMinutes())}:00`
  }

  const handleTitleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setTitle(e.target.value)
      setTitleError(false)
    },
    [setTitle, setTitleError]
  )

  const handleWebinarIdChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setWebinarId(e.target.value.replaceAll(/\s/g, ''))
    },
    [setWebinarId]
  )

  const handleQAndAEnabled = useCallback(() => {
    setQAndAEnabled(v => !v)
  }, [setQAndAEnabled])

  const handleStartDateChange = useCallback(
    (date: Date) => {
      setStartDate(date)
      setEndDate(date)
      setDateRangeError(false)
      setPastDateError(false)
    },
    [setStartDate, setDateRangeError]
  )

  const handleEndDateChange = useCallback(
    (date: Date) => {
      setEndDate(date)
      setDateRangeError(false)
    },
    [setEndDate, setDateRangeError]
  )

  const handleStartTimeChange = useCallback(
    (time: Date) => {
      setStartTime(time)
      // set date in case time crosses date boundary
      setStartDate(time)
      setDateRangeError(false)

      if (endTime < time) {
        const endDate = new Date(time)
        endDate.setHours(endDate.getHours() + 1)
        setEndTime(endDate)
        setEndDate(endDate)
      }
    },
    [setStartTime, setDateRangeError, endTime]
  )

  const handleEndTimeChange = useCallback(
    (time: Date) => {
      setEndTime(time)
      // set date in case time crosses date boundary
      setEndDate(time)
      setDateRangeError(false)
    },
    [setEndTime, setDateRangeError]
  )

  const handleEventAction = async (notify: boolean, draft: boolean, hidden = false): Promise<[boolean, string]> => {
    const variables: EventUpdateProps = {
      eventStart: formatDate(startDate, startTime),
      eventEnd: formatDate(endDate, endTime),
      draft: draft,
      title: title ?? '',
      location: location ?? '',
      description: description?.html,
      hidden: hidden ?? null,
      date: getStartOfCurrentDay(),
      notify: notify && !draft,
    }
    if (actingSysAdmin) {
      variables.isLivestream = isLivestream ?? false
      variables.qAndAEnabled = qAndAEnabled ?? false
      variables.googleCalendar = googleCalendar
      setGoogleCalendarError(null)
      if (webinarId && isLivestream) {
        variables.webinarId = webinarId
      }
    }
    if (event) {
      const newData = await editEvent({
        variables: { ...variables, eventId: event?.eventId || '', userId: authUserId ?? '' },
      })
      if (newData?.data?.editEvent?.errors) {
        for (const e of newData.data.editEvent.errors) {
          console.error('error', e)
          const code = e?.code ?? ''
          if (code.includes('googleCalendar')) {
            setGoogleCalendarError(e?.message ?? 'Google Calendar Error')
          }
          if (code.includes('zoomLivestreamFailed')) {
            onClose()
            const errMsg = e?.message ?? 'Zoom Livestream Error'
            return [false, `Event updated. ${errMsg}`]
          }
        }
        console.error('new data', newData)
        return [false, '']
      } else {
        onClose()
        return [true, '']
      }
    } else {
      const newData = await addEvent({ variables: { ...variables, communityId, userId: authUserId ?? '' } })
      if (newData?.data?.addEvent?.errors) {
        for (const e of newData.data.addEvent.errors) {
          console.error('error', e)
          const code = e?.code ?? ''
          if (code.includes('googleCalendar')) {
            setGoogleCalendarError(e?.message ?? 'Google Calendar Error')
          }
          if (code.includes('zoomLivestreamFailed')) {
            onClose()
            const errMsg = e?.message ?? 'Zoom Livestream Error'
            return [false, `Event created. ${errMsg}`]
          }
        }
        console.error('new data', newData)
      }
      if (newData?.data?.addEvent?.data) {
        onClose()
        return [true, '']
      } else {
        return [false, '']
      }
    }
  }

  const validateEvent = () => {
    const fullStartDate = formatDate(startDate, startTime)
    const fullEndDate = formatDate(endDate, endTime)
    let valid = true

    if (!title) {
      valid = false
      setTitleError(true)
    }
    if (fullEndDate <= fullStartDate) {
      valid = false
      setDateRangeError(true)
    }
    // if creating a new event, cannot set time to be in the past
    if (!event && fullStartDate < formatDate(new Date(), new Date())) {
      valid = false
      setPastDateError(true)
    }
    return valid
  }

  const saveEvent = async (e: SyntheticEvent, notify: boolean, draft: boolean) => {
    setButtonsDisabled(true)
    const [success, message] = await handleEventAction(notify, draft, false)
    setButtonsDisabled(false)
    if (success) {
      let toastMessage: string
      if (message) {
        toastMessage = message
      } else {
        toastMessage = event ? 'Event updated successfully' : 'Event created successfully'
      }
      setToast?.(toastMessage)
    } else {
      let toastMessage: string
      if (message) {
        toastMessage = message
      } else {
        toastMessage = event ? 'Event update failed' : 'Event create failed'
      }
      setToast?.(toastMessage)
    }
    elementClicked(e, `click-community-event-save`, {
      community_id: communityId,
      event_id: event?.eventId,
    })
  }

  const checkEvent = () => {
    setButtonsDisabled(true)
    const valid = validateEvent()
    setButtonsDisabled(false)
    if (valid) {
      setShowNotifyDialog(true)
    }
  }

  const repostedComms = () => {
    if (event?.reposts?.length == 1) {
      return (
        <span>
          <EventRepostLink event={reposts?.[0]} />.
        </span>
      )
    } else if (event?.reposts?.length == 2) {
      return (
        <span>
          <EventRepostLink event={reposts?.[0]} /> and <EventRepostLink event={reposts?.[1]} />.
        </span>
      )
    } else if (event?.reposts?.length == 3) {
      return (
        <span>
          <EventRepostLink event={reposts?.[0]} />, <EventRepostLink event={reposts?.[1]} />, and{' '}
          <EventRepostLink event={reposts?.[2]} />.
        </span>
      )
    } else {
      return (
        <span>
          <EventRepostLink event={reposts?.[0]} />, <EventRepostLink event={reposts?.[1]} />, and{' '}
          <Link to={`./${event?.eventId}/repost`}>{reposts?.length ? reposts?.length - 2 : 0} other</Link> communities.
        </span>
      )
    }
  }

  return (
    <div className="edit-event-container">
      <div className={'top-items'}>
        <h3>{event ? `Edit ${isDraft ? 'Draft ' : ' '}Event` : 'Create Event'}</h3>
        {event?.lastNotifiedTime ? (
          <div className={`event-notification-statement ${isCondensedPortrait ? ' condensed' : ''}`}>
            Last notification sent <TimeAgo time={event?.lastNotifiedTime} />
          </div>
        ) : (
          <div className={`event-notification-statement ${isCondensedPortrait ? ' condensed' : ''}`}>
            No notification has been sent.
          </div>
        )}
      </div>
      <div className="title">
        <h6>Event Title</h6>
        <Form.Control
          placeholder="Title this event"
          maxLength={200}
          value={title}
          onChange={handleTitleTextChange}
          bsPrefix={' '}
        />
        {titleError && <FormError message={'Title is required'} />}
      </div>
      <div className={`date-time-pickers ${isCondensedPortrait ? 'condensed' : ''}`}>
        <div className="picker-row">
          <div className="picker">
            <h6>Start Date</h6>
            <ReactDatePicker
              id="event-start-date"
              minDate={new Date()}
              selected={startDate}
              onChange={handleStartDateChange}
            />
          </div>
          <div className="picker">
            <h6>End Date</h6>
            <ReactDatePicker
              id="event-end-date"
              minDate={new Date()}
              selected={endDate}
              onChange={handleEndDateChange}
            />
          </div>
        </div>
        <div className="picker-row">
          <div id="start-time-picker" className="picker">
            <h6>Start Time</h6>
            <TimeSelector
              selectedTime={startTime}
              selectedDate={startDate}
              interval={15}
              onSelect={handleStartTimeChange}
            />
          </div>
          <div id="end-time-picker" className="picker">
            <h6>End Time</h6>
            <TimeSelector selectedTime={endTime} selectedDate={endDate} interval={15} onSelect={handleEndTimeChange} />
          </div>
          {!isCondensedPortrait && (
            <div id="timezone-picker" className="picker">
              <h6>Time Zone</h6>
              <Form.Control className="timezone" value={timeZone} readOnly bsPrefix={' '} />
            </div>
          )}
        </div>
        {isCondensedPortrait && (
          <div className="picker-row">
            <div id="timezone-picker" className="picker timezone-picker">
              <h6>Time Zone</h6>
              <Form.Control className="timezone" value={timeZone} readOnly bsPrefix={' '} />
            </div>
          </div>
        )}
      </div>
      {dateRangeError && <FormError message={'Event end must be after event start.'} />}
      {pastDateError && <FormError message={'Event cannot be created in the past.'} />}
      <div className="location">
        <h6>Location</h6>
        <Form.Control
          placeholder="Address or Meeting Link"
          value={location ?? ''}
          onChange={event => setLocation(event.target.value)}
          bsPrefix={' '}
        />
      </div>
      {actingSysAdmin && (
        <div className="google-calendar">
          <h6>Google Account for Calendar</h6>
          <Form.Control
            placeholder="veevaconnect@veeva.com"
            value={googleCalendar ?? ''}
            onChange={event => setGoogleCalendar(event.target.value)}
            bsPrefix={' '}
          />
          {Boolean(googleCalendarError) && <FormError message={googleCalendarError ?? ''} />}
        </div>
      )}

      {actingSysAdmin && (
        <div className="isLivestream">
          <h6>Livestream</h6>
          <div className="livestream-checkboxes">
            <div className={'form-check'}>
              <div>
                <label>
                  <input
                    type={'radio'}
                    name={'livestreamRadio'}
                    defaultChecked={livestreamSelection === 'none'}
                    onChange={handleNoLivestream}
                    className={'form-check-input'}
                  />
                  No Livestream
                </label>
              </div>
              <div>
                <label>
                  <input
                    type={'radio'}
                    name={'livestreamRadio'}
                    defaultChecked={livestreamSelection === 'webinar'}
                    onChange={handleWebinarLivestream}
                    className={'form-check-input'}
                  />
                  Zoom Livestream
                </label>
                <div className={'webinar-id'}>
                  <label>
                    <h6>Webinar ID</h6>
                    <input
                      type={'text'}
                      disabled={livestreamSelection !== 'webinar'}
                      placeholder={'000000000000'}
                      value={webinarId}
                      onChange={handleWebinarIdChange}
                      className={'form-input'}
                    />
                  </label>
                </div>
              </div>
              <div>
                <label>
                  <input
                    type={'radio'}
                    name={'livestreamRadio'}
                    defaultChecked={livestreamSelection === 'direct'}
                    onChange={handleDirectLivestream}
                    className={'form-check-input'}
                  />
                  Direct Livestream
                </label>
              </div>
            </div>
          </div>
        </div>
      )}
      {actingSysAdmin && isLivestream && (
        <div className="isLivestream">
          <h6>Livestream Q&amp;A</h6>
          <div className="livestream-checkboxes">
            <div className={'form-check'}>
              <label>
                <input
                  type={'checkbox'}
                  defaultChecked={qAndAEnabled}
                  onChange={handleQAndAEnabled}
                  className={'form-check-input'}
                />
                Q and A
              </label>
            </div>
          </div>
        </div>
      )}
      <div className="description">
        <h6>Description</h6>
        <QuillEditor<Maybe<Html>>
          toolbar={QuillToolbar.Limited}
          placeholder="Describe this event"
          initialHtml={description}
          setHtml={setDescription}
        />
      </div>
      {!isCondensedPortrait && (
        <div className={`button-container ${!event || event?.draft ? '' : 'simple-actions'}`}>
          <div className="left-buttons">
            {(event?.draft || !event) && (
              <Button
                variant="outline-secondary"
                disabled={buttonsDisabled}
                className={'save-draft'}
                onClick={e => {
                  void saveEvent(e, false, true)
                  elementClicked(e, 'click-event-draft-save', { communityId: communityId, eventId: event?.eventId })
                }}
                role="button"
              >
                <span>Save Draft</span>
              </Button>
            )}
          </div>
          <span className="actions">
            <Button variant="light" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={checkEvent} disabled={buttonsDisabled}>
              Save
            </Button>
          </span>
        </div>
      )}
      {isCondensedPortrait && (
        <div className={'top-items condensed'}>
          <div className="left-buttons">
            {(event?.draft || !event) && (
              <Button
                variant="outline-secondary"
                disabled={buttonsDisabled}
                className={'save-draft'}
                onClick={e => {
                  void saveEvent(e, false, true)
                  elementClicked(e, 'click-event-draft-save', { communityId: communityId, eventId: event?.eventId })
                }}
                role="button"
              >
                <span>Save Draft</span>
              </Button>
            )}
          </div>
          <span className="actions">
            <Button variant="light" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={checkEvent} disabled={buttonsDisabled}>
              Save
            </Button>
          </span>
        </div>
      )}
      {event && (
        <div className={`bottom-row${hasReposts ? '' : ' no-reposts'}`}>
          {Boolean(hasReposts) && (
            <div className={'repost-statement'}>
              <i>This event has been reposted in {repostedComms()}</i>
            </div>
          )}
        </div>
      )}
      <Modal show={showNotifyDialog} onHide={() => setShowNotifyDialog(false)} className={'save'}>
        <Modal.Header closeButton />
        <Modal.Body>
          <p className={'message'}>Do you want to notify community members about this event?</p>
          {Boolean(hasReposts) && (
            <p className={'sub-message'}>Notifications will also be sent for {reposts?.length} event reposts.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="save-btn" onClick={e => saveEvent(e, false, false)}>
            Save
          </Button>
          <Button variant="primary" size="sm" onClick={e => saveEvent(e, true, false)} disabled={buttonsDisabled}>
            Save and notify
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default CommunityEventEdit
